import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, ValidationErrors } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import FormSelectBoardComponent from 'webapp/components/form/select-board';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import ReportsShareScheduleFormComponent from 'webapp/components/reports/share/schedule/form';
import RowErrorComponent from 'webapp/components/row-error';

import type { ReportScheduleSetting } from 'webapp/models-custom';

type ValidationKeys = 'target' | 'deliveryOrRecipients';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: ReportScheduleSetting;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
	onContentChange?: () => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormReportScheduleSettingMainComponent extends Component<MainComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);

		if (!this.args.model.reportId) {
			const firstReportId = this.itemsList[0]?.value;
			if (firstReportId) {
				this.args.model.reportId = firstReportId;
			}
		}
	}

	get itemsList() {
		return this.mmReportsService.normalReportModels.map((report) => {
			return {
				value: report.numericId!,
				label: report.title,
				label2: report.createdText,
				avatar: report.image || report.fallbackImage,
				bgTheme: report.bgTheme,
				color: report.color,
				count: report.slideCount,
			};
		});
	}

	@action
	changeReportId(reportId: number) {
		this.args.model.reportId = reportId;
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.args.model;

		model.validate(); // to cleanup general errors

		if (!model.reportId) {
			errors.target = [{ message: 'Must choose a report!' }];
		}

		if (!model.isEmailDelivery && !model.isSlackDelivery) {
			// not shown in the form bcos of the custom fieldname
			// same errors will be handled by the sub-components
			// we need this here bcos form validation must fail
			errors.deliveryOrRecipients = [
				{
					message: 'At least one delivery type / recipients must be selected',
				},
			];
		}

		return errors;
	}

	@action
	save() {
		const model = this.args.model;

		model.cleanupUnusedScheduleValues();

		return model.save().then(() => {
			const trackingData = model.getTrackingDataForCreate(this.args.origin);
			this.analyticsService.event(TrackingEvent.SCHEDULED_REPORT_SENT, trackingData);
		});
	}

	<template>
		{{#if @model.isNew}}
			<div class="row row--board-selector">
				<FormSelectBoardComponent
					name="report"
					@selected={{@model.reportId}}
					@options={{this.itemsList}}
					@onChange={{this.changeReportId}}
					@placeholder="Select report"
					@isNumeric={{true}}
					@isRequired={{true}}
					@extraClass="cs-image"
					@dropdownSearchPlaceholder="Search"
					@showFloatlabel={{false}}
					@tooltipOnSelectionFromProperty=""
					@isDisabled={{@submitInProgress}}
				/>
				<RowErrorComponent @items={{@errors.target}} />
			</div>
		{{/if}}

		<ReportsShareScheduleFormComponent
			@model={{@model}}
			@isPeriodic={{true}}
			@errors={{@errors}}
			@submitInProgress={{@submitInProgress}}
			@onContentChange={{@onContentChange}}
		/>
	</template>
}
